import React, { useState } from 'react';
import { FiTrash2, FiPlus } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

interface EditableListProps {
  items: string[];
  onChange: (items: string[]) => void;
  title: string;
  placeholder?: string;
  maxItems?: number;
  disabled?: boolean;
  fontSize?: 'small' | 'medium' | 'large';
  /** data-map-id prefix for map navigation (e.g. 'tethics' → items get 'tethics_0', 'tethics_1') */
  mapIdPrefix?: string;
  /** Called when the 🗺 button is clicked for an item */
  onShowInMap?: (mapId: string) => void;
}

const EditableList: React.FC<EditableListProps> = ({
  items,
  onChange,
  title,
  placeholder = 'Add new item...',
  maxItems,
  disabled = false,
  fontSize = 'medium',
  mapIdPrefix,
  onShowInMap,
}) => {
  const { darkMode } = useTheme();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');

  // Start editing an item
  const startEditing = (index: number) => {
    if (disabled) return;
    setEditingIndex(index);
    setEditValue(items[index]);
  };

  // Save the current edit
  const saveEdit = () => {
    if (editingIndex === null || !editValue.trim()) return;
    
    const newItems = [...items];
    newItems[editingIndex] = editValue.trim();
    onChange(newItems);
    setEditingIndex(null);
    setEditValue('');
  };

  // Cancel the current edit
  const cancelEdit = () => {
    setEditingIndex(null);
    setEditValue('');
  };

  // Delete an item
  const deleteItem = (index: number) => {
    if (disabled) return;
    const newItems = [...items];
    newItems.splice(index, 1);
    onChange(newItems);
  };

  // Add a new item
  const addItem = () => {
    if (disabled || (maxItems && items.length >= maxItems)) return;
    
    // Add empty item and start editing it
    const newItems = [...items, ''];
    onChange(newItems);
    setEditingIndex(newItems.length - 1);
    setEditValue('');
  };

  // Handle key presses in the input
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      saveEdit();
    } else if (e.key === 'Escape') {
      cancelEdit();
    }
  };

  // Get font size classes based on selected option
  const getFontSizeClasses = (size: 'small' | 'medium' | 'large') => {
    switch (size) {
      case 'small':
        return 'text-xs';
      case 'medium':
        return 'text-sm';
      case 'large':
        return 'text-base';
      default:
        return 'text-sm';
    }
  };

  const fontSizeClass = getFontSizeClasses(fontSize);

  return (
    <div className={`rounded-md ${darkMode ? 'bg-gray-800/50' : 'bg-gray-50'} border ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      {/* Header with title and count */}
      <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
        <h3 className={`text-xs font-bold uppercase tracking-wider ${fontSize === 'small' ? 'text-xs' : fontSize === 'large' ? 'text-sm' : 'text-xs'}`}>
          {title} ({items.length})
        </h3>
      </div>
      
      {/* List items */}
      <div className="p-2">
        {items.length === 0 ? (
          <div className={`italic ${fontSizeClass} ${darkMode ? 'text-gray-400' : 'text-gray-500'} p-2`}>
            No items added yet.
          </div>
        ) : (
          <ul className="space-y-1">
            {items.map((item, index) => (
              <li 
                key={index} 
                className={`rounded-md ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'} transition-colors`}
              >
                {editingIndex === index ? (
                  <div className="flex p-2">
                    <input
                      type="text"
                      className={`flex-grow px-2 py-1 rounded-md ${fontSizeClass} ${
                        darkMode 
                          ? 'bg-gray-700 text-white border-gray-600' 
                          : 'bg-white text-gray-800 border-gray-300'
                      } border`}
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onBlur={saveEdit}
                      autoFocus
                    />
                  </div>
                ) : (
                  <div
                    className="flex justify-between items-center p-2"
                    data-map-id={mapIdPrefix ? `${mapIdPrefix}_${index}` : undefined}
                  >
                    <span 
                      className={`flex-grow cursor-pointer ${fontSizeClass}`}
                      onClick={() => startEditing(index)}
                    >
                      {item}
                    </span>
                    {onShowInMap && mapIdPrefix && (
                      <button
                        onClick={() => onShowInMap(`${mapIdPrefix}_${index}`)}
                        className="text-gray-400 hover:text-teal-500 transition-colors mr-1"
                        title="Show in map"
                        aria-label="Show in map"
                      >
                        🗺
                      </button>
                    )}
                    <button
                      onClick={() => deleteItem(index)}
                      className={`text-gray-400 hover:text-red-500 transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                      disabled={disabled}
                      aria-label="Delete item"
                    >
                      <FiTrash2 size={16} />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
        
        {/* Add button */}
        {(!maxItems || items.length < maxItems) && (
          <button
            onClick={addItem}
            className={`mt-2 w-full p-2 rounded-md border border-dashed flex items-center justify-center ${fontSizeClass}
              ${darkMode 
                ? 'border-gray-600 text-gray-400 hover:text-blue-400 hover:border-blue-400' 
                : 'border-gray-300 text-gray-500 hover:text-blue-500 hover:border-blue-500'
              } transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={disabled}
          >
            <FiPlus className="mr-1" />
            <span>Add Item</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default EditableList;
